import api from './api'

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
}

export interface ChatResponse {
  success: boolean
  response: string
  sessionId: string
  score: number
  leadData?: any
  shouldNotifyAgent: boolean
  chatClosed?: boolean
  closeReason?: string
}

// Configurar interceptor para headers de idioma
api.interceptors.request.use(
  (config) => {
    const browserLanguage = navigator.language || navigator.languages?.[0] || 'es-MX'
    config.headers['Accept-Language'] = browserLanguage
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

function detectUserLanguage(): string {
  const browserLang = navigator.language || navigator.languages?.[0] || 'es'
  if (browserLang.startsWith('es')) return 'es'
  if (browserLang.startsWith('en')) return 'en'
  return 'es'
}

function getBrowserContext() {
  return {
    userAgent: navigator.userAgent,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    languages: navigator.languages || [navigator.language]
  }
}

export const chatService = {
  async sendMessage(agentSubdomain: string, message: string, sessionId?: string, propertyId?: string): Promise<ChatResponse> {
    const detectedLanguage = detectUserLanguage()
    const browserContext = getBrowserContext()
    
    console.log(`🌍 Language detected: ${detectedLanguage} (Browser: ${navigator.language})`)

    const payload = {
      message,
      sessionId,
      propertyId,
      language: detectedLanguage,
      browserContext: {
        timezone: browserContext.timezone,
        primaryLanguage: browserContext.language,
        supportedLanguages: browserContext.languages
      }
    }
    
    try {
      const response = await api.post(`/api/chat/${agentSubdomain}/message`, payload)
      console.log(`💬 Message sent - Language: ${detectedLanguage}, Score: ${response.data.score}`)
      return response.data
    } catch (error) {
      console.error('❌ Chat service error:', error)
      throw error
    }
  },

  async closeSession(agentSubdomain: string, sessionId: string, reason: 'user_closed' | 'user_left' | 'timeout' = 'user_closed'): Promise<void> {
    try {
      await api.post(`/api/chat/${agentSubdomain}/close-session`, {
        sessionId,
        reason
      })
      console.log(`🔒 Session ${sessionId} closed - Reason: ${reason}`)
    } catch (error) {
      console.error('❌ Close session error:', error)
      throw error
    }
  },

  async checkActivity(sessionId: string): Promise<boolean> {
    try {
      const response = await api.post(`/api/chat/check-activity/${sessionId}`)
      return response.data.isActive
    } catch (error) {
      console.error('❌ Check activity error:', error)
      return false
    }
  }
}

export function generateSessionId(): string {
  const timestamp = Date.now()
  const randomStr = Math.random().toString(36).substring(2, 11)
  return `session_${timestamp}_${randomStr}`
}

export function formatMessageTime(timestamp: Date): string {
  return new Intl.DateTimeFormat('default', {
    hour: '2-digit',
    minute: '2-digit'
  }).format(timestamp)
}

export function isValidMessage(message: string): boolean {
  return typeof message === 'string' && message.trim().length > 0 && message.trim().length <= 1000
}
